import { kebabCase } from "lodash";
import queryString from "query-string";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";

import {
  BusinessAction,
  openDispatchingModal,
  setBusinessImportMapping,
} from "actions";

import {
  ADD_CONSIGNMENT_OFFLINE,
  BUSINESS,
  CREATE_BUSINESS_FROM_IMPORT,
  PATCH_BUSINESS_FROM_IMPORT,
  PATCH_CONSIGNMENT_OFFLINE,
  REQUERY_ABN_FOR_BUSINESS,
} from "constants/actionTypes";
import { ModalTypes } from "constants/navigation";
import { Settings } from "constants/settings";

import { splitName } from "lib";

import {
  getReturnTo,
  getSaleyardName,
  openEditBusinessModal,
} from "lib/navigation";
import { getDefaultPropertyId } from "lib/properties";
import toast from "lib/toast";

import {
  getBusinessById,
  getCurrentSaleyardId,
  getSetting,
  selectPropertyEnrichedBusinessByBusinessIdLookup,
  selectPropertyIdByPICLookup,
} from "selectors";

import { api } from "./api";

const addressFields = [
  "addressingInformation",
  "route",
  "state",
  "streetNumber",
  "locality",
  "latitude",
  "longitude",
  "postalCode",
];

function* createBusinessFromImport(action) {
  const tempBusinessId = uuidv4();
  const { payload, externalCode, source } = action;
  const {
    businessName,
    businessShortCode,
    businessPIC,
    abn,
    email,
    phoneNumber,
    altPhoneNumber,
    auctionsPlusId,
    contactFullName,
  } = payload;
  const values = {
    id: tempBusinessId,
    name: businessName,
  };
  if (businessShortCode) {
    // Set both the saleyard code and the agent code to the same thing.
    values.shortCodeSaleyard = businessShortCode;
    values.shortCode = businessShortCode;
  }

  if (auctionsPlusId) {
    values.shortCodeAuctionsPlus = auctionsPlusId;
  }
  if (abn) {
    values.abn = abn.replace(/\D/g, "");
  }
  if (businessPIC) {
    values.properties = [
      {
        PIC: businessPIC,
        name: null,
      },
    ];
  }

  values.address = {};
  addressFields.forEach(addressField => {
    if (payload[addressField]) {
      values.address[addressField] = payload[addressField];
    }
  });

  if (email) {
    values.emailRecipients = [];
    // If there are contact details, and add if they don't exist.
    // Some values may come in as a comma, pipe or semicolon
    email
      .split(/[;|,]/)
      .map(emailAddress => emailAddress.trim())
      .forEach(emailAddress => {
        const emailRecipient = {
          email: emailAddress,
          phoneNumber: phoneNumber || altPhoneNumber || undefined,
          isCommercialReportRecipient: true,
          isComplianceReportRecipient: true,
          firstName: "-",
          lastName: "-",
        };
        // Try and break full name up.
        if (contactFullName) {
          const splitFullName = splitName(contactFullName);
          emailRecipient.firstName = splitFullName.firstName || "-";
          emailRecipient.lastName = splitFullName.lastName || "-";
        }
        values.emailRecipients.push(emailRecipient);
      });
  }

  yield put(BusinessAction.create(values));
  if (source && externalCode) {
    yield put(setBusinessImportMapping(source, externalCode, tempBusinessId));
  }
}

function* patchBusinessFromImport(action) {
  const { businessId, payload, shouldOverrideShortCode } = action;

  const state = yield select();
  const business =
    selectPropertyEnrichedBusinessByBusinessIdLookup(state)[businessId];

  const {
    businessShortCode,
    businessPIC,
    auctionsPlusId,
    phoneNumber,
    contactFullName,
    email,
  } = payload;

  // only if user has accepted to update short codes; should we
  if (shouldOverrideShortCode !== false) {
    // If a shortcode was provided, use it.
    if (businessShortCode) {
      // If the business doesnt have an (agent) (short_) code set, set it to the same value.
      if (!business.shortCode) {
        business.shortCode = businessShortCode;
      }
      business.shortCodeSaleyard = businessShortCode;
    }

    // If we've got AuctionsPlus data, use it.
    if (auctionsPlusId) {
      business.shortCodeAuctionsPlus = auctionsPlusId;
    }
  }

  // If a PIC was provided, and it's not already there, add it to the business.
  if (businessPIC) {
    const propertyId = selectPropertyIdByPICLookup(state)[businessPIC];
    if (propertyId) {
      // Known property - see if it's already attached to the business.
      if (!business.properties.find(property => property.id === propertyId)) {
        business.properties = [
          ...business.properties,
          {
            id: propertyId,
            default: false,
            name: "",
          },
        ];
      }
    } else {
      // Totally new property (to this user)
      business.properties = [
        ...business.properties,
        {
          PIC: businessPIC,
          default: false,
          name: "",
        },
      ];
    }
  }

  // Import address - only if they don't already have one.
  // they'll have to manage change of addresses manually.
  if (
    !business.address ||
    (!business.address.streetNumber &&
      !business.address.postalCode &&
      !business.address.state)
  ) {
    business.address = {};
    addressFields.forEach(addressField => {
      if (payload[addressField] && !business.address[addressField]) {
        business.address[addressField] = payload[addressField];
      }
    });
  }

  // If there are contact details, and add if they don't exist.
  // Some values may come in as a comma, pipe or semicolon
  email
    .split(/[;|,]/)
    .map(emailAddress => emailAddress.trim())
    .forEach(emailAddress => {
      if (
        emailAddress &&
        !business.emailRecipients.find(er => er.email === emailAddress)
      ) {
        const emailRecipient = {
          email: emailAddress,
          phoneNumber,
          isCommercialReportRecipient: true,
          isComplianceReportRecipient: true,
          firstName: "-",
          lastName: "-",
        };
        // Try and break full name up.
        if (contactFullName) {
          const splitFullName = splitName(contactFullName);
          emailRecipient.firstName = splitFullName.firstName || "-";
          emailRecipient.lastName = splitFullName.lastName || "-";
        }
        business.emailRecipients.push(emailRecipient);
      }
    });

  yield put(BusinessAction.update(business));
}

function* updateVendorDefaultConsigningDeploymentId(
  vendorId,
  saleyardName,
  deploymentId,
) {
  // TODO - test/check/validate this comment from the old code.
  // We must use the sale from the action, as the Consign To
  // action may be creating a consignment in a different Livestock Sale
  // saleyardName,
  yield put(
    BusinessAction.update({
      id: vendorId,
      default_consigning_deployment_id: deploymentId,
    }),
  );
}

function* onAddConsignment(action) {
  const { meta, payload, options = {} } = action;
  const { consigned_from_id: consignedFromId, vendor_id: vendorId } = payload;
  const { sale } = meta.offline.commit.meta;

  const { shouldUpdateVendorDefaultConsigningDeployment } = options;

  if (consignedFromId && shouldUpdateVendorDefaultConsigningDeployment) {
    yield updateVendorDefaultConsigningDeploymentId(
      vendorId,
      sale.saleyard_name,
      consignedFromId,
    );
  }
}

function* onPatchConsignment(action) {
  const { options = {}, payload } = action;
  const { vendor_id: vendorId, consigned_from_id: consignedFromId } = payload;

  const { shouldUpdateVendorDefaultConsigningDeployment } = options;
  // a patch may not contain the vendor
  if (vendorId) {
    // only update the Vendor's defaultConsigningDeploymentId property when there is an updated value in the patch
    if (consignedFromId && shouldUpdateVendorDefaultConsigningDeployment) {
      yield updateVendorDefaultConsigningDeploymentId(
        vendorId,
        getSaleyardName(),
        consignedFromId,
      );
    }
  }
}

function onRequeryAbnForBusinessRequest() {
  toast.syncing("Re-Checking ABN Status", {
    autoClose: false,
  });
}

function* onRequeryAbnForBusinessSuccess(action) {
  const { meta: { id } = {} } = action;
  yield put(BusinessAction.requestOne({ id }));
  toast.dismiss(kebabCase("Re-Checking ABN Status"));
  toast.success("ABN Status Synced");
}

function onRequeryAbnForBusinessFailure(action) {
  const { error } = action;
  toast.dismiss(kebabCase("Re-Checking ABN Status"));
  toast.error(`An error occurred requerying abn. ${error}`);
}

function* onSuggestBuyerWayPropertyAction(action) {
  const { propertyId, buyerWayName, businessId } = action;
  if (!propertyId || !buyerWayName || !businessId) {
    return;
  }
  const state = yield select();

  if (!getSetting(Settings.suggestBuyerWayProperties)(state)) {
    return;
  }

  const business = getBusinessById(businessId)(state);
  if (!business) {
    return;
  }

  const saleyardId = getCurrentSaleyardId(state) || null;

  const defaultPropertyId = getDefaultPropertyId(
    business,
    saleyardId,
    buyerWayName,
  );

  if (defaultPropertyId === propertyId) {
    // this is already the default property
    return;
  }

  const modalArgs = {
    businessId,
    buyerWayName,
    propertyId,
    saleyardId,
  };

  yield put(
    openDispatchingModal(
      ModalTypes.DefaultBuyerWayProperty,
      getReturnTo(),
      null,
      modalArgs,
    ),
  );
}

function* performBusinessSearchRequest(action, endpoint, options) {
  const { onSuccess, onError } = {
    ...options,
  };

  try {
    const responsePromise = yield call(api.get, endpoint);
    const response = yield responsePromise;
    const results = yield response;

    typeof onSuccess === "function" && onSuccess(results);
  } catch (e) {
    try {
      typeof onError === "function" && onError(e);
    } catch (onErrorError) {
      // eslint-disable-next-line no-console
      console.log("There was an error handling the error", onErrorError);
    }
    // eslint-disable-next-line no-console
    console.log("There was an error", e);
    yield call(api.handleFetchError, e, "business search.", action);
  }
}

function* onGetBusinessesForAutoComplete(action) {
  const { userRole, term, options } = action;
  const endpoint = queryString.stringifyUrl({
    url: `/v2/businesses/autocomplete/`,
    query: { user_role: userRole, term },
  });

  yield performBusinessSearchRequest(action, endpoint, options);
}

function* onGetGlobalBusinessesForAutoComplete(action) {
  const { term, options } = action;

  const endpoint = queryString.stringifyUrl({
    url: `/v2/businesses/global-search/`,
    query: { term },
  });

  yield performBusinessSearchRequest(action, endpoint, options);
}

function onGetOrCreateGlobalBusinessSuccess(action) {
  openEditBusinessModal(action.id);
}

export default function* rootSaga() {
  yield takeEvery(
    REQUERY_ABN_FOR_BUSINESS.REQUEST,
    onRequeryAbnForBusinessRequest,
  );
  yield takeEvery(
    REQUERY_ABN_FOR_BUSINESS.SUCCESS,
    onRequeryAbnForBusinessSuccess,
  );
  yield takeEvery(
    REQUERY_ABN_FOR_BUSINESS.FAILURE,
    onRequeryAbnForBusinessFailure,
  );

  yield takeEvery(CREATE_BUSINESS_FROM_IMPORT, createBusinessFromImport);
  yield takeEvery(PATCH_BUSINESS_FROM_IMPORT, patchBusinessFromImport);

  yield takeLatest(ADD_CONSIGNMENT_OFFLINE, onAddConsignment);
  yield takeLatest(PATCH_CONSIGNMENT_OFFLINE, onPatchConsignment);

  yield takeEvery(
    BUSINESS.SUGGEST_BUYER_WAY_PROPERTY.ACTION,
    onSuggestBuyerWayPropertyAction,
  );

  yield takeEvery(
    BUSINESS.GET_BUSINESSES_FOR_AUTO_COMPLETE.REQUEST,
    onGetBusinessesForAutoComplete,
  );

  yield takeEvery(
    BUSINESS.GET_GLOBAL_BUSINESSES_FOR_AUTO_COMPLETE.REQUEST,
    onGetGlobalBusinessesForAutoComplete,
  );

  yield takeEvery(
    BUSINESS.GET_OR_CREATE_GLOBAL_BUSINESS.SUCCESS,
    onGetOrCreateGlobalBusinessSuccess,
  );
}
